import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo, useState} from 'react';
import { DateFormatHelper } from "../../../../../utilities/helpers/DateFormatHelper";
import MoneyInput from "../../../common/MoneyInput";
import { UseCurrencyDecorator } from "../../../../../utilities/hooks/UseCurrencyDecorator";
import { useProviderCurrency } from "../../../../../utilities/hooks/useProviderCurrency";
import {TextFormatHelper} from "../../../../../utilities/helpers/TextFormatHelper";
import CustomCheckbox from "../../../common/CustomCheckbox";
import ChannelIcon from "../../../common/ChannelIcon";
import { AmountFormatHelper } from '../../../../../utilities/helpers/AmountFormatHelper';
import { useSubscription } from '../../../../../utilities/hooks/useSubscription';

const ConfirmRequestDialog = ({
	onConfirm,
	detail,
	setShowDialog,
}) => {
	const dictionary = useDictionary();
	const isSubscription = useSubscription();
	const [isCustomAmount, setIsCustomAmount] = useState(dictionary.original_amount);
	const [customAmount, setCustomAmount] = useState(0);
	const [changeReason, setChangeReason] = useState("");
	const [isOverBooking, setIsOverBooking] = useState(false);
	const [isAllowOverBooking, setIsAllowOverBooking] = useState(true);
	const [acceptAsOffline, setAcceptAsOffline] = useState(false);


	useEffect(() => {
		detail && setIsOverBooking(detail?.overbooking);
		setAcceptAsOffline(false);
		setCustomAmount(AmountFormatHelper.formatAmount(detail?.amount));
	}, [detail]);

	const isOfflineChannel = (booking) =>
		Number(booking?.channel_id || booking?.experience_marketplace_channel_id) === 4 ||
		booking?.channel === 'Offline';
	const isOfflineBooking = isOfflineChannel(detail);
	const canAcceptOfflinePayment = isSubscription && isOfflineBooking;
	const offlinePaymentDisabledReason = isSubscription
		? dictionary.accept_as_offline_offline_channel_required
		: dictionary.accept_as_offline_business_plan_required;

	const appendOfflineAcceptance = (data) => {
		if (!canAcceptOfflinePayment || !acceptAsOffline) {
			return data;
		}

		return {
			...data,
			accept_as_offline: acceptAsOffline
		};
	};

	const currencyDecorator = UseCurrencyDecorator();
	const providerCurrency = useProviderCurrency();
	const currency = detail?.currency ?? providerCurrency;
	const detailLabelStyle = { marginBottom: 8, fontWeight: 600, flexShrink: 0 };
	const detailValueStyle = { marginBottom: 8, marginLeft: 8, fontWeight: 400 };
	const onCancelClicked = () => {
		setShowDialog(false);
	};
	const onConfirmClicked = () => {
		const data =
			isCustomAmount === dictionary.original_amount
				? {status: "confirmed", is_custom_amount: false, allow_overbooking: isAllowOverBooking }
				: {status: "confirmed", is_custom_amount: true, custom_amount: AmountFormatHelper.deFormatAmount(Number(customAmount)), custom_amount_note: changeReason, allow_overbooking: isAllowOverBooking };
		onConfirm(appendOfflineAcceptance(data));
		setShowDialog(false);
	};

	const onRadioChanged = (e) => {
		setIsCustomAmount(e.target.value);
	};
	const onItemBlur = () => {
		const newValue = customAmount >= 5 ? customAmount : 5;
		setCustomAmount(newValue);
	};

	const isDisabled = useMemo(() => {
		if(isCustomAmount === dictionary.original_amount){
			return false;
		}
		return (changeReason === "");
	}, [isCustomAmount, changeReason]);

	return (
		<div className="dialog-wrapper" onClick={(e) => e.stopPropagation()}>
			<div className="dialog-container">
				<div className="dialog-title-container">
					<div className="dialog-title">
						{dictionary.booking_confirmation}
					</div>
					<div className="dialog-close-button" onClick={onCancelClicked} />
				</div>
				<div className="dialog-title-divider" style={{ marginBottom: 0 }} />
				<div className="dialog-content-container">
					<div className='d-flex'>
						<label className='nowrap' style={detailLabelStyle}>
							{dictionary.experience_title}:
						</label>
						<label style={detailValueStyle}>
							{detail?.experience?.title}
						</label>
					</div>
					<div className='d-flex'>
						<label style={detailLabelStyle}>
							{dictionary.customer}:
						</label>
						<label style={detailValueStyle}>
							{detail?.customer_name ? detail?.customer_name : (detail?.customer.name || '')}
						</label>
					</div>
					<div className='d-flex'>
						<label style={detailLabelStyle}>
							{dictionary.date_time}:
						</label>
						<label style={detailValueStyle}>
							{DateFormatHelper.rangeDayMonthYearFormat(
								detail?.slot?.start_time,
								detail?.slot?.end_time
							)}
						</label>
					</div>
					<div className='d-flex'>
						<label style={detailLabelStyle}>
							{dictionary.notes}:
						</label>
						<label style={detailValueStyle}>
							{detail?.notes?.request_availability?.customer || 'No notes'}
						</label>
					</div>
					{detail?.channel ? (
						<div className='d-flex'>
							<label style={detailLabelStyle}>
								{dictionary.channel}:
							</label>
							<label style={{ ...detailValueStyle, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
								<ChannelIcon channelId={detail.channel_id || detail.experience_marketplace_channel_id} />
								{detail.channel}
							</label>
						</div>
					) : null}

					<div style={{ marginTop: 24, marginBottom: 24 }}>
						<CustomCheckbox
							label={dictionary.accept_as_offline}
							checked={canAcceptOfflinePayment && acceptAsOffline}
							setChecked={setAcceptAsOffline}
							disabled={!canAcceptOfflinePayment}
						/>
						{!canAcceptOfflinePayment ? (
							<div
								style={{ marginTop: 4, marginLeft: 26, opacity: 0.7, fontSize: 12 }}
								className="text"
							>
								{offlinePaymentDisabledReason}
							</div>
						) : null}
					</div>

					<label style={{ marginBottom: 8, marginTop: 0, fontWeight: 500 }}>
						{dictionary.total_cost}:
					</label>

					<div>
						<label className="radio-label">
							{dictionary.original_amount}: {currencyDecorator(AmountFormatHelper.formatAmount(detail?.amount), currency)}
							<input
								type="radio"
								value={dictionary.original_amount}
								checked={isCustomAmount === dictionary.original_amount}
								onChange={onRadioChanged}
							/>
							<span className="checkmark" />
						</label>
					</div>
					<div className="d-flex items-center">
						<label className="radio-label">
							{dictionary.change_amount}:
							<input
								type="radio"
								value={dictionary.change_amount}
								checked={isCustomAmount === dictionary.change_amount}
								onChange={onRadioChanged}
							/>
							<span className="checkmark" />
						</label>
						{isCustomAmount === dictionary.change_amount &&
							<div className="group-inputs" style={{ width: '120px', marginTop: 0, marginBottom: 10	, marginLeft: 12 }}>
								<MoneyInput
									min={AmountFormatHelper.formatAmount(detail?.amount)}
									value={customAmount}
									setValue={(e) => setCustomAmount(e.target.value)}
									onBlur={onItemBlur}
									currencyCode={currency?.code ?? 'EUR'}
								/>
							</div>
						}
					</div>
					<div className="slot-detail-content" style={{ marginBottom: 24 }}>
						{isCustomAmount === dictionary.change_amount ?
						<div className="group-inputs">
							<div className="text">{dictionary.reason_for_change}:</div>
							<textarea
								placeholder={dictionary.describe_reason_change}
								disabled={isCustomAmount !== dictionary.change_amount}
								value={changeReason}
								onChange={(e) => setChangeReason(e.target.value)}
								data-testid="experience-name-input"
							/>
						</div>
							: null}
						{isOverBooking ?
						<CustomCheckbox
							label={dictionary.allow_overbooking}
							checked={isAllowOverBooking}
							setChecked={setIsAllowOverBooking}
						/> : null }
					</div>

				</div>

				<div className="dialog-title-divider" style={{ margin: 0 }} />
				<div
					className="dialog-buttons-container"
					style={{
						padding: 12,
						marginBottom: 0,
						alignSelf: 'center',
						width: '100%'
					}}
				>
					<button onClick={onCancelClicked} className="tertiary-button">
						{dictionary.cancel}
					</button>
					<button
						disabled={isDisabled}
						onClick={onConfirmClicked}
						className="button"
					>
						{dictionary.confirm}
					</button>
				</div>
			</div>
		</div>
	);
};

ConfirmRequestDialog.propTypes = {
	setShowDialog: PropTypes.func,
	onConfirm: PropTypes.func,
	detail: PropTypes.object
};

export default ConfirmRequestDialog;
